import React, { useState, useEffect, useMemo } from "react";
import "./inflmain.css";

const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

const InflMain = ({ orders }) => {
    const [discountCodesByMonth, setDiscountCodesByMonth] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [influencerData, setInfluencerData] = useState({});
    const [influencerOrders, setInfluencerOrders] = useState({});
    const [influencerAllOrders, setInfluencerAllOrders] = useState({});
    const [programBonuses, setProgramBonuses] = useState({});
    const [isFetchingCollections, setIsFetchingCollections] = useState(false);
    const [lastFetchTime, setLastFetchTime] = useState(null);

    // List of excluded codes
    const excludedCodes = [
        "BYMO5", "BYMO30RON", "0MZQ66BF1JDE", "CW28", "CODROSU", "NOUBYMO5",
        "BYMOH559", "TESTAFFX", "TEST2PERF95", "5LEI", "CUPONERIA10", "SPECIAL40",
        "BF40", "BF30", "BF20", "BU20", "b20", "toto100", "MB10", "crazydeals10", "BYMOSTUDENT"
    ];

    // Function to fetch collections data and save to sessionStorage
    const fetchAndSaveCollections = async () => {
        setIsFetchingCollections(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/collections`);
            if (!response.ok) {
                throw new Error(`API request failed with status ${response.status}`);
            }
            
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || "Failed to fetch influencer data");
            }
            
            // Save to sessionStorage
            sessionStorage.setItem('influencerCollections', JSON.stringify(data.collections));
            
            // Update last fetch time
            const now = new Date();
            sessionStorage.setItem('influencerCollectionsLastFetch', now.toISOString());
            setLastFetchTime(now);
            
            // Process the data
            processInfluencerData(data.collections);
            
            alert('Influencer data refreshed successfully!');
        } catch (error) {
            console.error("Error fetching influencer data:", error);
            alert(`Error refreshing data: ${error.message}`);
        } finally {
            setIsFetchingCollections(false);
        }
    };

    // Process influencer data from collections
    const processInfluencerData = (collections) => {
        const influencerInfo = {};
        const emailToCode = {};
        const programData = {};
        
        // Extract program bonuses first
        Object.keys(collections).forEach(email => {
            const programDoc = collections[email].find(doc => doc.id === "000program");
            if (programDoc && programDoc.initial_bonus) {
                const program = programDoc.program || "Unknown";
                programData[program] = {
                    initial_bonus: parseFloat(programDoc.initial_bonus) || 0
                };
            }
        });
        
        setProgramBonuses(programData);
        
        // Extract influencer info from collections
        Object.keys(collections).forEach(email => {
            // Look for the "00info" document in each collection
            const infoDoc = collections[email].find(doc => doc.id === "00info");
            const programDoc = collections[email].find(doc => doc.id === "000program");
            
            if (infoDoc && infoDoc["Cod Reducere"]) {
                const discountCode = infoDoc["Cod Reducere"].toUpperCase();
                const program = infoDoc.program || (programDoc ? programDoc.program : "Unknown");
                
                influencerInfo[discountCode] = {
                    email,
                    program,
                    name: infoDoc.name || infoDoc.Name || email,
                    initial_bonus: programDoc && programDoc.initial_bonus 
                        ? parseFloat(programDoc.initial_bonus) 
                        : (programData[program] ? programData[program].initial_bonus : 0)
                };
                
                // Map email to discount code for order matching
                emailToCode[email.toLowerCase()] = discountCode;
            }
        });
        
        setInfluencerData(influencerInfo);
        
        // Process orders for each influencer
        if (orders && orders.length > 0) {
            const ordersByInfluencer = {};
            const allOrdersByInfluencer = {};
            
            // For each influencer email, find their orders
            Object.entries(emailToCode).forEach(([email, discountCode]) => {
                const storeCreditsOrders = findStoreCreditsOrdersByEmail(email);
                const allOrders = findAllOrdersByEmail(email);
                
                if (storeCreditsOrders.length > 0) {
                    ordersByInfluencer[discountCode] = storeCreditsOrders;
                }
                
                if (allOrders.length > 0) {
                    allOrdersByInfluencer[discountCode] = allOrders;
                }
            });
            
            setInfluencerOrders(ordersByInfluencer);
            setInfluencerAllOrders(allOrdersByInfluencer);
        }
    };

    // Function to find all orders by email - Now filters out voided orders
    const findAllOrdersByEmail = (email) => {
        if (!orders || orders.length === 0) return [];
        
        return orders
            .filter(order => {
                // Check if the order is from this email
                if (!order.email || order.email.toLowerCase() !== email.toLowerCase()) {
                    return false;
                }
                
                // Filter out voided orders
                return order.financial_status !== "voided";
            })
            .map(order => ({
                id: order.id,
                name: order.name || order.order_number,
                date: new Date(order.created_at),
                amount: parseFloat(order.current_total_price || order.total_price) || 0,
                status: order.financial_status,
                created_at: order.created_at
            }));
    };

    // Format order details for tooltip
    const formatOrdersForTooltip = (orders) => {
        if (!orders || orders.length === 0) return "No orders";
        
        // Sort orders by date (newest first)
        const sortedOrders = [...orders].sort((a, b) => 
            new Date(b.date) - new Date(a.date)
        );
        
        // Format each order with ID, date and amount
        return sortedOrders.map(order => {
            const date = new Date(order.created_at).toLocaleDateString();
            return `#${order.id} (${date}): ${Math.round(order.amount)} RON - ${order.status}`;
        }).join('\n');
    };

    // Existing function to find store credit orders by email
    const findStoreCreditsOrdersByEmail = (email) => {
        if (!orders || orders.length === 0) return [];
        
        return orders
            .filter(order => {
                // Check if the order is from this email
                if (!order.email || order.email.toLowerCase() !== email.toLowerCase()) {
                    return false;
                }
                
                // Filter out voided orders
                if (order.financial_status === "voided") {
                    return false;
                }
                
                // Check for store credit payment
                // First check if payment_details exists and is an array
                if (!order.payment_details || !Array.isArray(order.payment_details)) {
                    return false;
                }
                
                // Look for any payment method that includes "store_credit"
                return order.payment_details.some(payment => {
                    const paymentMethod = payment.payment_method || '';
                    return paymentMethod === "store_credit" || 
                           paymentMethod.includes("store_credit") ||
                           paymentMethod.toLowerCase().includes("credit");
                });
            })
            .map(order => ({
                id: order.id,
                date: new Date(order.created_at),
                amount: parseFloat(order.current_total_price || order.total_price) || 0,
                paymentDetails: order.payment_details
            }));
    };

    // Load influencer data from sessionStorage only
    useEffect(() => {
        const loadInfluencerData = () => {
            // Try to get data from sessionStorage
            const cachedData = sessionStorage.getItem('influencerCollections');
            const lastFetch = sessionStorage.getItem('influencerCollectionsLastFetch');
            
            if (cachedData) {
                try {
                    const collections = JSON.parse(cachedData);
                    processInfluencerData(collections);
                    
                    if (lastFetch) {
                        setLastFetchTime(new Date(lastFetch));
                    }
                } catch (error) {
                    console.error("Error parsing cached influencer data:", error);
                    setInfluencerData({});
                }
            }
            
            // Process orders data regardless of whether we have influencer data
            processOrdersData();
        };
        
        loadInfluencerData();
    }, [orders]);

    // Process orders to extract discount codes usage by month
    const processOrdersData = () => {
        if (!orders || orders.length === 0) {
            setIsLoading(false);
            return;
        }

        const codesByMonth = {};
        const monthTotals = {};

        // Process each order
        orders.forEach(order => {
            if (!order.created_at || !order.discount_codes || order.discount_codes.length === 0) {
                return;
            }

            const createdAt = new Date(order.created_at);
            const year = createdAt.getFullYear();
            const month = createdAt.getMonth() + 1; // 1-based month
            const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
            
            // Initialize month data if not exists
            if (!codesByMonth[monthKey]) {
                codesByMonth[monthKey] = {};
                monthTotals[monthKey] = {
                    amount: 0,
                    count: 0
                };
            }

            // Get the total order amount
            const orderTotal = parseFloat(order.current_total_price || order.total_price) || 0;
            
            // Process each discount code in the order
            order.discount_codes.forEach(discount => {
                const code = discount.code.toUpperCase();
                
                // Skip excluded codes
                if (excludedCodes.includes(code)) {
                    return;
                }
                
                if (!codesByMonth[monthKey][code]) {
                    codesByMonth[monthKey][code] = {
                        totalOrderAmount: 0,
                        count: 0
                    };
                }
                
                codesByMonth[monthKey][code].totalOrderAmount += orderTotal;
                codesByMonth[monthKey][code].count += 1;
                monthTotals[monthKey].amount += orderTotal;
                monthTotals[monthKey].count += 1;
            });
        });

        // Convert to a more usable format for the table
        const result = {
            months: Object.keys(codesByMonth).sort((a, b) => b.localeCompare(a)), // Sort newest first
            codes: {},
            monthTotals
        };

        // Get all unique discount codes
        const allCodes = new Set();
        Object.values(codesByMonth).forEach(monthData => {
            Object.keys(monthData).forEach(code => {
                allCodes.add(code);
            });
        });

        // Organize data by discount code
        allCodes.forEach(code => {
            result.codes[code] = {};
            result.months.forEach(month => {
                result.codes[code][month] = codesByMonth[month]?.[code] || { 
                    totalOrderAmount: 0, 
                    count: 0 
                };
            });
        });

        setDiscountCodesByMonth(result);
        setIsLoading(false);
    };

    // Sort codes by total order amount (descending)
    const sortedCodes = useMemo(() => {
        if (!discountCodesByMonth.codes) return [];
        
        return Object.entries(discountCodesByMonth.codes)
            .map(([code, monthData]) => {
                const totalOrderAmount = Object.values(monthData)
                    .reduce((sum, data) => sum + data.totalOrderAmount, 0);
                    
                return { code, totalOrderAmount };
            })
            .sort((a, b) => b.totalOrderAmount - a.totalOrderAmount)
            .map(item => item.code);
    }, [discountCodesByMonth]);

    // Calculate total personal orders for each influencer
    const getInfluencerOrdersTotal = (code) => {
        if (!influencerOrders[code] || influencerOrders[code].length === 0) {
            return { count: 0, amount: 0 };
        }
        
        const orders = influencerOrders[code];
        const count = orders.length;
        const amount = orders.reduce((sum, order) => sum + order.amount, 0);
        
        return { count, amount };
    };

    // Get all orders total for each influencer
    const getInfluencerAllOrdersTotal = (code) => {
        if (!influencerAllOrders[code] || influencerAllOrders[code].length === 0) {
            return { count: 0, amount: 0 };
        }
        
        const orders = influencerAllOrders[code];
        const count = orders.length;
        const amount = orders.reduce((sum, order) => sum + order.amount, 0);
        
        return { count, amount };
    };

    // Calculate net value (Total - All Orders - Initial Bonus)
    const calculateNetValue = (code, totalOrderAmount) => {
        const influencerInfo = influencerData[code] || {};
        const allOrders = getInfluencerAllOrdersTotal(code);
        const initialBonus = influencerInfo.initial_bonus || 0;
        
        return totalOrderAmount - allOrders.amount - initialBonus;
    };

    // Calculate grand totals
    const calculateGrandTotals = useMemo(() => {
        if (!discountCodesByMonth.monthTotals) return { amount: 0, count: 0 };
        
        return Object.values(discountCodesByMonth.monthTotals).reduce(
            (totals, monthTotal) => ({
                amount: totals.amount + monthTotal.amount,
                count: totals.count + monthTotal.count
            }),
            { amount: 0, count: 0 }
        );
    }, [discountCodesByMonth]);

    // Format the last fetch time
    const formatLastFetchTime = () => {
        if (!lastFetchTime) return "Never";
        
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        }).format(lastFetchTime);
    };

    if (isLoading) {
        return <div className="loading-indicator">Loading influencer data...</div>;
    }

    return (
        <div className="infl-container">
            <div className="infl-header-container">
                <h1 className="infl-header">Influencer Discount Codes Usage</h1>
                <div className="infl-controls">
                    <div className="last-fetch-info">
                        Last data refresh: {formatLastFetchTime() || "Never"}
                    </div>
                    <button 
                        className="refresh-button" 
                        onClick={fetchAndSaveCollections}
                        disabled={isFetchingCollections}
                    >
                        {isFetchingCollections ? "Refreshing..." : "Refresh Influencer Data"}
                    </button>
                </div>
            </div>
            
            {!discountCodesByMonth.months || discountCodesByMonth.months.length === 0 ? (
                <div className="no-data-message">No discount codes found in orders.</div>
            ) : (
                <table className="infl-table">
                    <thead>
                        <tr>
                            <th className="table-header"></th>
                            <th className="table-header"></th>
                            <th className="table-header">Personal Orders</th>
                            {discountCodesByMonth.months.map(month => {
                                const monthData = discountCodesByMonth.monthTotals[month];
                                return (
                                    <th key={`total-${month}`} className="table-header-top">
                                        {Math.round(monthData.amount).toLocaleString()}
                                        <br />
                                        <small>📦 {monthData.count}</small>
                                    </th>
                                );
                            })}
                            <th className="table-header-top">
                                {Math.round(calculateGrandTotals.amount).toLocaleString()}
                                <br />
                                <small>📦 {calculateGrandTotals.count}</small>
                            </th>
                            <th className="table-header-top">Net</th>
                        </tr>
                        <tr>
                            <th className="table-header">Discount Code</th>
                            <th className="table-header">Program</th>
                            <th className="table-header">All Orders</th>
                            {discountCodesByMonth.months.map(month => {
                                const [year, monthNum] = month.split('-');
                                return (
                                    <th key={month} className="table-header">
                                        {`${year.slice(-2)}'-${monthNum}`}
                                    </th>
                                );
                            })}
                            <th className="table-header">Total</th>
                            <th className="table-header">Net</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedCodes.map(code => {
                            const codeData = discountCodesByMonth.codes[code];
                            const totalOrderAmount = Object.values(codeData)
                                .reduce((sum, data) => sum + data.totalOrderAmount, 0);
                            const totalCount = Object.values(codeData)
                                .reduce((sum, data) => sum + data.count, 0);
                            const influencerInfo = influencerData[code] || {};
                            const allOrders = getInfluencerAllOrdersTotal(code);
                            const allOrdersList = influencerAllOrders[code] || [];
                            const allOrdersTooltip = formatOrdersForTooltip(allOrdersList);
                            const netValue = calculateNetValue(code, totalOrderAmount);
                            const initialBonus = influencerInfo.initial_bonus || 0;
                                
                            return (
                                <tr key={code}>
                                    <td className="code-cell">
                                        {code}
                                        {influencerInfo.email && (
                                            <div className="email-info">{influencerInfo.email}</div>
                                        )}
                                    </td>
                                    <td className="program-cell">
                                        {influencerInfo.program || "Unknown"}
                                        {initialBonus > 0 && (
                                            <div className="program-bonus">Bonus: {Math.round(initialBonus)}</div>
                                        )}
                                    </td>
                                    <td 
                                        className="table-cell"
                                        title={allOrdersTooltip}
                                        style={{ cursor: 'help' }}
                                    >
                                        {allOrders.count > 0 ? (
                                            <>
                                                {Math.round(allOrders.amount).toLocaleString()}
                                                <br />
                                                <small>📦 {allOrders.count}</small>
                                            </>
                                        ) : (
                                            influencerInfo.email ? (
                                                <span className="no-orders">No orders</span>
                                            ) : "-"
                                        )}
                                    </td>
                                    {discountCodesByMonth.months.map(month => {
                                        const data = codeData[month];
                                        return (
                                            <td key={`${code}-${month}`} className="table-cell">
                                                {data.totalOrderAmount > 0 ? (
                                                    <>
                                                        {Math.round(data.totalOrderAmount).toLocaleString()}
                                                        <br />
                                                        <small>({data.count} orders)</small>
                                                    </>
                                                ) : "-"}
                                            </td>
                                        );
                                    })}
                                    <td className="table-cell">
                                        <strong>{Math.round(totalOrderAmount).toLocaleString()}</strong>
                                        <br />
                                        <small>({totalCount} orders)</small>
                                    </td>
                                    <td className={`table-cell ${netValue < 0 ? 'negative-value' : 'positive-value'}`}>
                                        <strong>{Math.round(netValue).toLocaleString()}</strong>
                                    </td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td className="code-cell total-row">TOTAL</td>
                            <td className="program-cell total-row">-</td>
                            <td className="table-cell total-row">-</td>
                            {discountCodesByMonth.months.map(month => {
                                const monthData = discountCodesByMonth.monthTotals[month];
                                return (
                                    <td key={`total-${month}`} className="table-cell total-row">
                                        {Math.round(monthData.amount).toLocaleString()}
                                        <br />
                                        <small>📦 {monthData.count}</small>
                                    </td>
                                );
                            })}
                            <td className="table-cell total-row">
                                {Math.round(calculateGrandTotals.amount).toLocaleString()}
                                <br />
                                <small>📦 {calculateGrandTotals.count}</small>
                            </td>
                            <td className="table-cell total-row">-</td>
                        </tr>
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default InflMain; 